import { useEffect, useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { aboutInfo, faqList } from '@/mocks/comics';

const CONTACT_FORM_URL = 'https://readdy.ai/api/form/d9otj3qqs38sj6jc7fl0';

const orgSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: '八仔漫画网',
  url: 'https://www.bazai-comics.com',
  description: '国内领先的正版漫画在线阅读平台',
  foundingDate: '2018',
  contactPoint: {
    '@type': 'ContactPoint',
    telephone: '+86-400-888-6666',
    contactType: 'customer service',
    email: 'contact@bazai-comics.com',
  },
  address: {
    '@type': 'PostalAddress',
    streetAddress: '文一西路969号',
    addressLocality: '杭州',
    addressRegion: '浙江',
    addressCountry: 'CN',
  },
};

const aboutPageSchema = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: '关于八仔漫画网',
  description: aboutInfo.description,
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqList.map((faq) => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.answer,
    },
  })),
};

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  useEffect(() => {
    document.title = '关于我们 - 八仔漫画网';
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('phone_alt');
    if (honeypot && String(honeypot).trim() !== '') {
      setFormStatus('success');
      return;
    }

    const textarea = formData.get('message') as string;
    if (textarea && textarea.length > 500) {
      setFormError('留言内容不能超过500个字符。');
      setFormStatus('error');
      return;
    }

    try {
      formData.delete('phone_alt');
      const response = await fetch(CONTACT_FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText;
        if (serverMsg && serverMsg.includes('spam')) {
          setFormError('提交失败，请稍后重试。');
        } else {
          setFormError(serverMsg || '提交失败，请稍后重试。');
        }
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请稍后重试。');
      setFormStatus('error');
    }
  };

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: '关于我们' },
  ];

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(aboutPageSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
              <Breadcrumb items={breadcrumbItems} />

              <section className="mb-10">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-1 h-6 bg-primary-500 rounded-full"></div>
                  <h1 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">
                    关于八仔漫画网
                  </h1>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <div>
                    <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                      {aboutInfo.description}
                    </p>
                    <div className="mt-4 p-4 bg-primary-50 rounded-lg border border-primary-100">
                      <p className="text-sm text-primary-800 font-medium">
                        <strong>我们的使命：</strong>{aboutInfo.mission}
                      </p>
                    </div>
                    <div className="grid grid-cols-2 gap-3 mt-4">
                      {aboutInfo.values.map((v) => (
                        <div key={v} className="flex items-center gap-2 px-3 py-2 bg-background-100 rounded-lg">
                          <i className="ri-check-double-line text-accent-500"></i>
                          <span className="text-sm text-foreground-700">{v}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    {aboutInfo.stats.map((stat) => (
                      <div key={stat.label} className="bg-background-50 rounded-lg border border-background-200/70 p-4 md:p-5 text-center">
                        <p className="font-heading text-2xl md:text-3xl font-bold text-primary-500">{stat.value}</p>
                        <p className="text-xs text-foreground-500 mt-1">{stat.label}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </section>

              <section className="mb-10 py-10 bg-background-100 -mx-4 md:-mx-6 px-4 md:px-6">
                <div className="max-w-7xl mx-auto">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-1 h-6 bg-accent-500 rounded-full"></div>
                    <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-900">
                      用户口碑
                    </h2>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {aboutInfo.reviews.map((review, idx) => (
                      <div key={idx} className="bg-background-50 rounded-lg border border-background-200/70 p-4 md:p-5">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                            <span className="text-sm font-bold text-primary-600">{review.name.charAt(0)}</span>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-foreground-800">{review.name}</p>
                            <div className="flex items-center gap-0.5">
                              {Array.from({ length: 5 }, (_, i) => (
                                <i key={i} className={`text-xs ${i < review.rating ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-300'}`}></i>
                              ))}
                            </div>
                          </div>
                        </div>
                        <p className="text-sm text-foreground-600 leading-relaxed">{review.content}</p>
                        <p className="text-xs text-foreground-400 mt-2">{review.date}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </section>

              <section className="mb-10">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-1 h-6 bg-primary-500 rounded-full"></div>
                  <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-900">
                    联系我们
                  </h2>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 flex items-center justify-center bg-primary-100 rounded-lg">
                        <i className="ri-mail-line text-primary-500 text-lg"></i>
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-foreground-800">电子邮箱</h3>
                        <p className="text-sm text-foreground-500">{aboutInfo.contact.email}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 flex items-center justify-center bg-primary-100 rounded-lg">
                        <i className="ri-phone-line text-primary-500 text-lg"></i>
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-foreground-800">客服电话</h3>
                        <p className="text-sm text-foreground-500">{aboutInfo.contact.phone}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 flex items-center justify-center bg-primary-100 rounded-lg">
                        <i className="ri-map-pin-line text-primary-500 text-lg"></i>
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-foreground-800">公司地址</h3>
                        <p className="text-sm text-foreground-500">{aboutInfo.contact.address}</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-background-50 rounded-lg border border-background-200/70 p-4 md:p-6">
                    <h3 className="font-heading text-base font-bold text-foreground-900 mb-4">
                      发送消息
                    </h3>
                    <form onSubmit={handleSubmit} className="space-y-4" data-readdy-form>
                      <input
                        type="text"
                        name="phone_alt"
                        className="honeypot-field"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        readOnly
                      />
                      <div>
                        <label htmlFor="contact-name" className="block text-xs font-medium text-foreground-700 mb-1">姓名</label>
                        <input
                          id="contact-name"
                          name="name"
                          type="text"
                          required
                          className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:border-primary-300 transition-colors"
                          placeholder="请输入您的姓名"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-email" className="block text-xs font-medium text-foreground-700 mb-1">邮箱</label>
                        <input
                          id="contact-email"
                          name="email"
                          type="email"
                          required
                          className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:border-primary-300 transition-colors"
                          placeholder="请输入您的邮箱"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-message" className="block text-xs font-medium text-foreground-700 mb-1">留言</label>
                        <textarea
                          id="contact-message"
                          name="message"
                          required
                          maxLength={500}
                          rows={4}
                          className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:border-primary-300 transition-colors resize-none"
                          placeholder="请输入您的留言（最多500字）"
                        ></textarea>
                      </div>
                      {formStatus === 'success' && (
                        <p className="text-sm text-accent-600 font-medium">消息发送成功！我们会尽快回复您。</p>
                      )}
                      {formStatus === 'error' && (
                        <p className="text-sm text-red-500">{formError || '提交失败，请稍后重试。'}</p>
                      )}
                      <button
                        type="submit"
                        className="w-full px-5 py-2.5 bg-primary-500 text-white rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
                      >
                        发送消息
                      </button>
                    </form>
                  </div>
                </div>
              </section>

              <section className="pb-10">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-1 h-6 bg-primary-500 rounded-full"></div>
                  <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-900">
                    常见问题
                  </h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {faqList.map((faq, idx) => (
                    <details key={idx} className="group bg-background-50 rounded-lg border border-background-200/70 p-4 md:p-5">
                      <summary className="flex items-center justify-between cursor-pointer text-sm md:text-base font-medium text-foreground-800">
                        {faq.question}
                        <i className="ri-add-line group-open:hidden text-foreground-400"></i>
                        <i className="ri-subtract-line hidden group-open:block text-foreground-400"></i>
                      </summary>
                      <p className="mt-3 text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                    </details>
                  ))}
                </div>
              </section>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}